import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { KANG_WIDGET_TOGGLE } from '@/constants/feature-toggles/kang-widget';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { bootIntercom, isIntercomBooted, showIntercom, updateIntercomLanguage } from '@/utils/intercom';

// The real widget UI (launcher bubble / messenger) only exists after Intercom's widget script has
// been fetched and finished its API handshake — bootIntercom resolving just means our wrapper
// chunk ran. The facade must stay on screen until one of these containers is actually in the DOM,
// otherwise there is a dead zone with no bubble at all and clicks feel like they did nothing.
const INTERCOM_WIDGET_SELECTOR = '#intercom-container, .intercom-lightweight-app, iframe[name="intercom-launcher-frame"]';
const WIDGET_POLL_INTERVAL_MS = 250;
const WIDGET_POLL_TIMEOUT_MS = 15000;

type LauncherStatus = 'idle' | 'loading' | 'ready';

// Facade for the Intercom launcher: a lookalike bubble that renders without the SDK, so the
// affordance is there while the real messenger stays off the critical path. It is not in the server
// HTML: it only appears once the Kang toggle answers "off" (see hasKangAnswered below). The SDK boots on whichever comes first — a click on the facade (queues 'show', so the messenger
// opens as soon as the widget is ready) or the first organic interaction (pointerdown/keydown/wheel,
// boots silently). We deliberately do not listen to 'scroll': the browser fires it during scroll
// position restoration (reload mid-page, back/forward navigation) with no real user input, which
// would boot the SDK immediately and negate the deferral. 'wheel' only fires on genuine mouse-wheel
// or trackpad input, so it covers desktop scrolling without that false positive — touch scrolling is
// already covered by 'pointerdown' and keyboard scrolling by 'keydown'. Audit bots produce none of
// these, so Lighthouse never pays for the SDK.
export function IntercomLauncher() {
  const { t, i18n } = useTranslation('common');
  const [status, setStatus] = useState<LauncherStatus>(() => (isIntercomBooted() ? 'loading' : 'idle'));
  const [clicked, setClicked] = useState(false);
  // Os dois lançadores ocupam o mesmo canto e nenhum negocia espaço com o outro, então
  // o toggle do Kang decide qual atende: ligado, ele; desligado ou inexistente, este.
  const { isEnabled: isKangEnabled, isLoading: isKangLoading } = useFeatureToggle({ key: KANG_WIDGET_TOGGLE, autoFetch: true });
  // Sem valor do servidor o toggle nasce `false` e carregando, e "ainda não sei" não é
  // "desligado": tratado assim, a fachada ia no HTML do SSR e piscava antes do Kang. A
  // primeira resposta fica registrada porque o polling de toggles refaz a busca (`isLoading`
  // volta a true) e, com o Kang desligado, a fachada sumiria e voltaria a cada invalidação.
  const [hasKangAnswered, setHasKangAnswered] = useState(false);
  useEffect(() => {
    if (!isKangLoading) setHasKangAnswered(true);
  }, [isKangLoading]);
  const isHidden = isKangEnabled || !hasKangAnswered;

  useEffect(() => {
    // Sem esta guarda o SDK ainda subiria na primeira interação e mostraria o
    // lançador REAL do Intercom — esconder só a fachada não resolveria nada.
    if (isHidden || status !== 'idle') return undefined;
    const bootSilently = () => {
      setStatus('loading');
      bootIntercom(i18n.language || 'en');
    };
    const options: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener('pointerdown', bootSilently, options);
    window.addEventListener('keydown', bootSilently, options);
    window.addEventListener('wheel', bootSilently, options);
    return () => {
      window.removeEventListener('pointerdown', bootSilently);
      window.removeEventListener('keydown', bootSilently);
      window.removeEventListener('wheel', bootSilently);
    };
    // i18n.language is intentionally read at boot time only; later changes go through the update effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isHidden]);

  // Swap the facade for the real launcher only once the widget UI is actually in the DOM (with a
  // timeout escape hatch so a blocked widget script cannot pin the facade forever).
  useEffect(() => {
    if (status !== 'loading') return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const widgetVisible = document.querySelector(INTERCOM_WIDGET_SELECTOR) !== null;
      if (widgetVisible || Date.now() - startedAt > WIDGET_POLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        setStatus('ready');
      }
    }, WIDGET_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [status]);

  useEffect(() => {
    updateIntercomLanguage(i18n.language || 'en');
  }, [i18n.language]);

  if (isHidden) return null;

  if (status === 'ready') return null;

  const handleClick = () => {
    setClicked(true);
    setStatus('loading');
    // 'show' lands on the SDK's queue shim, so the messenger opens as soon as the widget is ready.
    showIntercom(i18n.language || 'en');
  };

  return (
    <button type='button' data-np-chat-launcher='' aria-label={t('support.chat_with_us')} aria-busy={clicked} onClick={handleClick} className={`fixed bottom-5 end-5 z-[2147483000] flex size-12 items-center justify-center rounded-full border-0 bg-[#aef55c] shadow-[0_4px_16px_rgba(0,0,0,0.35)] transition-transform duration-150 ${clicked ? 'animate-pulse cursor-wait' : 'cursor-pointer hover:scale-105'}`}>
      <svg viewBox='0 0 28 32' width='24' height='24' fill='#0d0d0d' aria-hidden='true'>
        <path d='M28 32s-4.714-1.855-8.527-3.34H3.437C1.54 28.66 0 27.026 0 25.013V3.644C0 1.633 1.54 0 3.437 0h21.125c1.898 0 3.437 1.632 3.437 3.645v18.404H28V32zm-4.139-11.982a.88.88 0 0 0-1.292-.105c-.03.026-3.015 2.681-8.57 2.681-5.486 0-8.517-2.636-8.571-2.684a.88.88 0 0 0-1.29.107 1.01 1.01 0 0 0-.219.708.992.992 0 0 0 .318.664c.142.128 3.537 3.15 9.762 3.15 6.226 0 9.621-3.022 9.763-3.15a.992.992 0 0 0 .317-.664 1.01 1.01 0 0 0-.218-.707z' />
      </svg>
    </button>
  );
}
